import React, { useEffect, useState } from "react";
import { ChefHat, Plus, Trash2, Save, Power, PowerOff, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const DEFAULT_DISHES = [
  { dish_name: "Romazava", emoji: "🍲", theme: "Le plat national revisité par les chefs gasy" },
  { dish_name: "Ravitoto", emoji: "🥘", theme: "Feuilles de manioc pilées à l'honneur" },
  { dish_name: "Koba",     emoji: "🥭", theme: "La pâtisserie traditionnelle en mode moderne" },
  { dish_name: "Mofo Gasy", emoji: "🍞", theme: "Le petit-déj' malgache sous toutes ses formes" },
  { dish_name: "Henakisoa sy amalona", emoji: "🐟", theme: "Porc & anguille à la mode côtière" },
  { dish_name: "Voanjobory sy Henakisoa", emoji: "🥜", theme: "Pois bambara & porc" },
];

const inputCls = "w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60";

const fmtLocal = (d) => d.toISOString().slice(0, 16);

export const AdminChallengesPanel = () => {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59);
    return {
      dish_name: "",
      emoji: "🍲",
      theme: "",
      description: "",
      banner_url: "",
      slug: "",
      starts_at: fmtLocal(now),
      ends_at:   fmtLocal(end),
      is_active: true,
    };
  });

  const load = () => {
    api.get("/admin/challenges").then(({ data }) => setList(data)).catch(() => {});
  };
  useEffect(load, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pickPreset = (d) => {
    setForm((f) => ({ ...f, dish_name: d.dish_name, emoji: d.emoji, theme: d.theme }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/challenges", {
        ...form,
        starts_at: form.starts_at.replace("T", " ") + ":00",
        ends_at:   form.ends_at.replace("T", " ") + ":00",
      });
      toast.success("Défi créé ✨");
      load();
      setForm((f) => ({ ...f, dish_name: "", theme: "", description: "", banner_url: "", slug: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur création");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/challenges/${c.challenge_id}`, { is_active: !Number(c.is_active) });
      load();
    } catch { toast.error("Erreur"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Supprimer « ${c.dish_name} » ?`)) return;
    try {
      await api.delete(`/admin/challenges/${c.challenge_id}`);
      toast.success("Supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-challenges-panel">
      {/* Creation form */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-red/5 to-brand-orange/5 p-5">
        <div className="flex items-center gap-2 mb-1">
          <ChefHat size={14} className="text-brand-orange" />
          <p className="text-[10px] uppercase tracking-widest text-brand-orange font-bold">Défi cuisine</p>
        </div>
        <h2 className="font-display text-2xl font-black mb-1">Lancer un nouveau défi</h2>
        <p className="text-sm text-white/60 mb-4">
          Les participants soumettent leurs tracks de catégorie « Cuisine », les visiteurs votent, le top 3 est mis en avant sur la home.
        </p>

        {/* Presets */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {DEFAULT_DISHES.map((d) => (
            <button
              key={d.dish_name}
              onClick={() => pickPreset(d)}
              className="tag-pill hover:bg-brand-orange/15 hover:border-brand-orange/40 hover:text-brand-orange transition-colors"
              data-testid={`preset-${d.dish_name}`}
            >
              {d.emoji} {d.dish_name}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="grid sm:grid-cols-[90px_1fr] gap-3">
          <Field label="Emoji">
            <input value={form.emoji} onChange={set("emoji")} className={inputCls} maxLength={6} />
          </Field>
          <Field label="Nom du plat *">
            <input required value={form.dish_name} onChange={set("dish_name")} className={inputCls} data-testid="challenge-dish-name" placeholder="Romazava" />
          </Field>

          <Field label="Slug" full>
            <input value={form.slug} onChange={set("slug")} className={inputCls} placeholder="auto-généré (ex: romazava)" />
          </Field>

          <Field label="Thème (accroche)" full>
            <input value={form.theme} onChange={set("theme")} className={inputCls} placeholder="Le plat national revisité par les chefs gasy" />
          </Field>

          <Field label="Description" full>
            <textarea value={form.description} onChange={set("description")} rows={2} className={inputCls} placeholder="Inspirations, variantes autorisées, conseil pour briller…" />
          </Field>

          <Field label="Bannière (URL image)" full>
            <input value={form.banner_url} onChange={set("banner_url")} className={inputCls} placeholder="https://…" />
          </Field>

          <Field label="Début *">
            <input type="datetime-local" value={form.starts_at} onChange={set("starts_at")} required className={inputCls} />
          </Field>
          <Field label="Fin *">
            <input type="datetime-local" value={form.ends_at} onChange={set("ends_at")} required className={inputCls} />
          </Field>

          <div className="sm:col-span-2 flex items-center gap-3 pt-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={set("is_active")} />
              <span>Actif immédiatement</span>
            </label>
            <button type="submit" disabled={creating} className="btn-primary ml-auto inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="challenge-create-btn">
              <Plus size={14} /> {creating ? "Création…" : "Créer le défi"}
            </button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="font-display text-lg font-black mb-3">Défis existants ({list.length})</h3>
        {list.length === 0 ? (
          <div className="text-sm text-white/50 py-6 text-center">Aucun défi créé pour l'instant.</div>
        ) : (
          <ul className="space-y-2">
            {list.map((c) => {
              const ended = new Date(c.ends_at).getTime() < Date.now();
              const active = Number(c.is_active) === 1 && !ended;
              return (
                <li key={c.challenge_id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`challenge-row-${c.slug}`}>
                  <span className="text-2xl shrink-0">{c.emoji || "🍲"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{c.dish_name}</p>
                    <p className="text-[11px] text-white/50 truncate flex items-center gap-2">
                      <Clock size={10} /> {new Date(c.starts_at).toLocaleDateString()} → {new Date(c.ends_at).toLocaleDateString()}
                      {" · "} {c.entries_count || 0} participation{(c.entries_count || 0) > 1 ? "s" : ""}
                      {" · "} {c.votes_count || 0} votes
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    active
                      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                      : ended
                        ? "bg-white/5 text-white/40 border border-white/10"
                        : "bg-white/5 text-white/50 border border-white/10"
                  }`}>
                    {ended ? "Terminé" : active ? "Actif" : "Inactif"}
                  </span>
                  <button onClick={() => toggle(c)} className="text-xs text-white/60 hover:text-white" data-testid={`toggle-${c.slug}`}>
                    {Number(c.is_active) === 1 ? <PowerOff size={14} /> : <Power size={14} />}
                  </button>
                  <button onClick={() => del(c)} className="text-red-400/70 hover:text-red-400" data-testid={`delete-${c.slug}`}>
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1 font-bold">{label}</label>
    {children}
  </div>
);

export default AdminChallengesPanel;
